/**
 * components/DetailBarangModal.js
 * Item detail as a roomy modal: info, stock, SET recipes, and full riwayat, plus the
 * existing actions (Ubah / Penyesuaian / Transformasi). Replaces the old DetailBarang page.
 * Closes via ✕, "Tutup", Esc, or click-outside. Reads live from `data`, so it refreshes
 * after an edit or adjustment made through the stacked modals.
 *
 * Section E3: a riwayat line's invoice number opens that transaction in a
 * TransactionDetailModal stacked on top of this one (rendered as a SIBLING of this modal's
 * overlay, never inside it — a click inside it would otherwise bubble to this overlay's
 * click-outside handler and close the item too). "Lihat transformasi" hands off to
 * `onOpenTransformation` (App closes this modal and opens Riwayat Transformasi on that run).
 */
import React, { useState } from "react";
import { fmtQty, today } from "../utils/idGenerators";
import { parseFitment } from "../utils/fitment";
import { useModalEsc, backdropClose } from "../utils/modalStack";
import { useAvgCosts, formatWac } from "../utils/wac";
import { useExpenses } from "./ExpensesContext";
import RiwayatList from "./RiwayatList";
import { LineName } from "./LineName";
import TransactionDetailModal from "./TransactionDetailModal";

function Row({ label, children }) {
  return (<><dt>{label}</dt><dd>{children}</dd></>);
}

export default function DetailBarangModal({ item, history, recipes = [], items = [], contacts = [], branches = [], siblings = [], transactions = [], salespeople = [], persons = [], onClose, onEdit, onAdjust, onOpenItem, onOpenTransformation }) {
  useModalEsc(onClose);
  const [openTxnId, setOpenTxnId] = useState(null);

  // Derived on demand, never persisted (T1a) — same computeAvgCosts the transaction form's
  // WAC hint uses, so the two surfaces can never disagree (PRD §4.4/§5.5). Shared memoization
  // wrapper (fix #5) — see utils/wac.js's own header comment.
  // A purchase's own costs (Tagihan/Potongan/linked Biaya Lain-Lain) go into HPP — utils/wac.js.
  const { expenses } = useExpenses();
  const avgCosts = useAvgCosts(transactions, expenses);

  if (!item) return null;

  const total = (Number(item.stokPusat) || 0) + (Number(item.stokCabang) || 0);
  const avgCost = avgCosts.get(item.internalCode) ?? null;
  const itemHistory = history.filter((h) => h.itemCode === item.internalCode);
  const byCode = new Map(items.map((it) => [it.internalCode, it]));
  const contactById = new Map(contacts.map((c) => [c.id, c]));
  const setRecipes = item.isSet === true
    ? recipes.filter((r) => r.type === "ASSEMBLE" && (r.outputs || []).some((o) => o.internalCode === item.internalCode))
    : [];

  const openTxn = openTxnId ? transactions.find((t) => t.id === openTxnId) : null;

  return (
    <>
    <div className="modal-overlay" role="dialog" aria-modal="true" onMouseDown={backdropClose(onClose)}>
      <div className="modal-box modal-box--detail" onMouseDown={(e) => e.stopPropagation()}>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Tutup">×</button>

        <div className="modal-detail__head">
          <h2 className="modal-title">
            {item.name}
            {item.isSet === true && <span className="chip chip--set" style={{ marginLeft: "var(--gap-md)" }}>SET</span>}
          </h2>
          <div className="page-sub mono">{item.internalCode}</div>
        </div>

        <div className="card">
          <div className="card__title">Informasi Barang</div>
          <dl className="detail-grid">
            <Row label="Kode Pasar">{(() => { const codes = item.marketCodes || []; return codes.length === 0 ? "—" : codes.map((code, i) => <span key={`${code}-${i}`} className="chip chip--muted" style={{ marginRight: "var(--gap-xs)" }}>{code}</span>); })()}</Row>
            <Row label="Tipe Mobil">{(() => { const cars = parseFitment(item.fitment); return cars.length === 0 ? "—" : cars.map((c) => <span key={c} className="chip chip--muted" style={{ marginRight: "var(--gap-xs)" }}>{c}</span>); })()}</Row>
            <Row label="Kategori">{item.category || "—"}</Row>
          </dl>
        </div>

        {item.notes ? (
          <div className="ket-callout">
            <div className="ket-callout__label">Keterangan</div>
            <div className="ket-callout__body">{item.notes}</div>
          </div>
        ) : null}

        <div className="card">
          <div className="card__title">Stok</div>
          <dl className="detail-grid">
            <Row label="Stok Pusat"><b>{fmtQty(item.stokPusat)}</b></Row>
            <Row label="Stok Cabang"><b>{fmtQty(item.stokCabang)}</b></Row>
            <Row label="Total"><b style={{ color: "var(--blue-deep)" }}>{fmtQty(total)}</b></Row>
            <Row label="Stok Minimum">{item.minStock == null ? "— (batas umum)" : fmtQty(item.minStock)}</Row>
            <Row label="Modal Rata² (WAC)">{avgCost != null ? `Rp ${formatWac(avgCost)}` : "—"}</Row>
          </dl>
        </div>

        <div className="card">
          <div className="card__title">Saudara (Substitusi) ({siblings.length})</div>
          {siblings.length === 0 ? (
            <div className="empty-state">Belum ada barang saudara.</div>
          ) : (
            siblings.map((code) => {
              const sib = byCode.get(code);
              if (!sib) {
                return (
                  <div key={code} className="picker__row">
                    <div className="picker__name mono">{code} · (tidak tersedia)</div>
                  </div>
                );
              }
              const sibTotal = (Number(sib.stokPusat) || 0) + (Number(sib.stokCabang) || 0);
              return (
                <div key={code} className="picker__row" onClick={() => onOpenItem(code)}>
                  <div className="picker__name">
                    <span className="mono">{code}</span> · {sib.name}
                  </div>
                  <div className="picker__meta">
                    Pusat {fmtQty(sib.stokPusat)} · Cabang {fmtQty(sib.stokCabang)} · Total {fmtQty(sibTotal)}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {item.isSet === true && (
          <div className="card">
            <div className="card__title">Resep ({setRecipes.length})</div>
            {setRecipes.length === 0 ? (
              <div className="empty-state">Belum ada resep untuk SET ini.</div>
            ) : (
              setRecipes.map((r) => (
                <div key={r.id} className="recipe-block">
                  <div className="recipe-name"><b>{r.name}</b></div>
                  {r.note ? <div className="page-sub" style={{ marginBottom: "var(--gap-sm)" }}>{r.note}</div> : null}
                  <ul className="recipe-lines">
                    {r.inputs.map((l, i) => (
                      <li key={i}>
                        <span className="mono">{l.internalCode}</span> ·{" "}
                        <LineName line={l} byCode={byCode} />
                        {" "}× <b>{fmtQty(l.qty)}</b>
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
        )}

        <div className="card">
          <div className="card__title">Riwayat ({itemHistory.length})</div>
          <RiwayatList
            entries={itemHistory} contactById={contactById} branches={branches}
            transactions={transactions}
            onOpenTransaction={(id) => setOpenTxnId(id)}
            onOpenTransformation={onOpenTransformation}
          />
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={() => onEdit(item)}>Ubah</button>
          <button className="btn" onClick={() => onAdjust(item)}>Penyesuaian Stok</button>
          <button className="btn btn-primary" onClick={onClose}>Tutup</button>
        </div>
      </div>
    </div>
    {openTxn && (
      <TransactionDetailModal
        txn={openTxn}
        items={items}
        contacts={contacts}
        salespeople={salespeople}
        persons={persons}
        branches={branches}
        today={today()}
        onClose={() => setOpenTxnId(null)}
      />
    )}
    </>
  );
}
